<!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
<template>
  <div v-show="visible" class="date-picker-modal" @mousedown.self="close">
    <div class="modal-content">
      <div class="modal-header">
        <ys-text type="title3" data-test="title">Recent Imagery</ys-text>
        <ys-button
          class="close"
          variant="ghost"
          size="small"
          data-test="close"
          @click="close"
        >
          <x-icon slot="icon" icon="close" width="16" height="16" />
        </ys-button>
      </div>
      <div class="modal-description">
        <ys-text type="body2-bold">
          View conditions on the ground, updated every two weeks.
        </ys-text>
      </div>
      <div class="modal-body">
        <div
          v-for="{ year, group } in groupedDates"
          :key="year"
          class="year-group"
        >
          <hr />
          <div class="year-label">{{ year }}</div>
          <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/interactive-supports-focus -->
          <div
            v-for="(range, idx) in group"
            :key="idx"
            role="button"
            :aria-label="`Select range ${formatRange(range)}`"
            :class="['date-range', { selected: isSelected(range) }]"
            @click="select(range)"
          >
            {{ formatRange(range) }}
            <span v-if="isSelected(range)" class="dot" width="8" height="8">
              <x-icon
                class="check-icon"
                icon="check"
                color="white"
                width="13"
              />
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<!-- eslint-disable vue/block-lang -->
<script setup>
import { computed } from 'vue'
import '@onxmaps/yellowstone-ui/ys-text'
import '@onxmaps/yellowstone-ui/ys-button'
import xIcon from '@/components/shared/x-icon'

const props = defineProps({
  visible: Boolean,
  dateRanges: {
    type: Array,
    required: true,
  },
  selectedRange: {
    type: [Object, null],
    required: true,
  },
})

const emit = defineEmits(['close', 'select'])

const groupedDates = computed(() => {
  const groups = {}
  props.dateRanges.forEach((range) => {
    const year = new Date(range.end).getUTCFullYear()
    if (!groups[year]) {
      groups[year] = []
    }
    groups[year].push(range)
  })
  // Return an array of { year, group } sorted by year descending
  return Object.entries(groups)
    .sort((a, b) => b[0] - a[0])
    .map(([year, group]) => ({ year, group }))
})

function formatRange(range) {
  // Format as "Month d - Month d, yyyy" using UTC
  const start = new Date(range.start)
  const end = new Date(range.end)
  const startFmt = start.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  })
  const endFmt = end.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
  return `${startFmt} - ${endFmt}`
}

function isSelected(range) {
  return (
    props.selectedRange &&
    props.selectedRange.start === range.start &&
    props.selectedRange.end === range.end
  )
}

function select(range) {
  emit('select', range)
}

function close() {
  emit('close')
}
</script>

<style lang="scss" scoped>
@use '@/styles/system/_shadow.scss';
@use '@/styles/system/_animation.scss';

.date-picker-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: var(--ys-color-background-primary);
  border-radius: var(--ys-radius-200);
  width: 400px;
  max-height: 77vh;
  overflow-y: auto;
  scrollbar-width: none;
  box-shadow: shadow.$Medium;
  padding-top: 0;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  position: sticky;
  top: 0;
  background: var(--ys-color-background-primary);
  z-index: 100;

  & ys-text {
    margin-top: var(--ys-spacing-500);
    padding: 0 var(--ys-spacing-250);
  }

  & .close {
    position: absolute;
    top: 8px;
    right: 8px;
  }
}

.modal-description {
  color: var(--ys-color-text-dim);
  margin-bottom: var(--ys-spacing-100);
  padding: 0 var(--ys-spacing-250);
}

.modal-body {
  padding: 0 var(--ys-spacing-250);
}

.year-group {
  margin-bottom: var(--ys-spacing-200);
}
.year-label {
  font-weight: bold;
  margin-bottom: var(--ys-spacing-100);
  color: var(--ys-color-text-tertiary);
  background: var(--ys-color-background-primary);
  z-index: 0;
  top: 65px;
  padding: var(--ys-spacing-100) 0;
  position: sticky;
}
.date-range {
  padding: var(--ys-spacing-100) var(--ys-spacing-150);
  border-radius: var(--ys-radius-100);
  cursor: pointer;
  margin-bottom: var(--ys-spacing-50);
  display: flex;
  align-items: center;
  border: 3px solid var(--ys-color-background-primary);
  transition: border-color animation.$timing_150 animation.$ease_standard;

  &:hover {
    border: 3px solid var(--ys-color-stroke-primary);
  }
}
.date-range.selected {
  border: 3px solid var(--ys-color-stroke-reverse);
  justify-content: space-between;
}
.dot {
  width: 13px;
  height: 13px;
  background: var(--ys-color-icon-primary);
  border-radius: 50%;
  margin-right: var(--ys-spacing-100);
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  .check-icon {
    margin-top: 2px;
  }
}
</style>
